import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TotalPairView } from '../../components/TotalPairView';
import { TableRow } from '../../../Invoices/InvoiceHub/components/Table/TableRow';
import { DashboardContext } from '../../contexts/DashboardContext';
import { mapDataForInvoiceWidget } from '../../utils/invoice.utils';
import { ITableRow as IInvoiceTableRow } from '../../../Invoices/InvoiceHub/components/Table/types';
import { VIEWS_MAP } from '../../../../router/constants';
import { WidgetLink } from '../../components/WidgetLink';
import { useStore } from '../../../../store';
import Heading from '../../../../generic-components/Heading';
import { ReactComponent as InvoiceIcon } from '../../../../assets/dashboard/invoice-icon.svg';
import { formatValueWithoutCurrency } from '../../../../helpers/currency.helper';
import { InvoicesBillsWidgetsSkeleton } from '../../components/skeletons/InvoicesBillsWidgetsSkeleton';
import { trackMixPanel } from '../../../../services/mixpanel.service';
import { InvoicesWidgetZeroState } from './InvoiceWidgetZeroState';

const columnKeys = ['invoice', 'client', 'dueDate', 'status', 'amount'];

const INVOICE_WIDGET_STATES = {
  ZERO: 'ZERO',
  EMPTY: 'EMPTY',
  NOT_EMPTY: 'NOT_EMPTY',
};

const testIdPrefix = 'InvoicesWidget';

export const InvoicesWidget = ({ isLoading }: { isLoading: boolean }) => {
  const getSummaryData = useStore((state) => state.getSummaryData);
  const { lastInvoices, totalInvoicedAmount, totalOutstandingInvoiceAmount } = useContext(DashboardContext);
  const { t } = useTranslation('dashboardFaceliftResourcesEN');
  const navigate = useNavigate();
  const summary = getSummaryData();
  const {
    actionFlags: { createInvoice },
  } = summary;
  const widgetState = createInvoice
    ? lastInvoices?.length > 0
      ? INVOICE_WIDGET_STATES.NOT_EMPTY
      : INVOICE_WIDGET_STATES.EMPTY
    : INVOICE_WIDGET_STATES.ZERO;
  const mappedRows = lastInvoices && mapDataForInvoiceWidget(lastInvoices.slice(0, 3));
  const handleInvoiceRowClick = (invoiceId: string) => {
    trackMixPanel('Act on Invoices Widget', { Action: 'Act on Invoice Widget Row' });
    setTimeout(() => {
      navigate(`/invoices?id=${invoiceId}`);
    }, 300);
  };
  const renderInvoicesTable = () => (
    <table className='w-full' data-testid={`${testIdPrefix}__table`}>
      <tbody>
        {mappedRows?.map((row: IInvoiceTableRow, index: number) => (
          <TableRow
            columnKeys={columnKeys}
            row={row}
            lastRow={null}
            isLastRow={index === 2}
            isWidgetMode
            key={row.externalId}
            onInvoiceRowClick={handleInvoiceRowClick}
          />
        ))}
      </tbody>
    </table>
  );
  const handleCreateFirstInvoiceClick = () => {
    trackMixPanel('Act on Invoices Widget', { Action: 'Create your first invoice' });
    setTimeout(() => {
      navigate(`/${VIEWS_MAP.INVOICES}`);
    }, 300);
  };
  const renderEmptyState = () => (
    <div className='flex flex-col items-center h-full'>
      <InvoiceIcon className='mt-[38px] w-[42px] h-[42px]' />
      <Heading
        title={t('INVOICES_WIDGET_EMPTY_STATE_TITLE')}
        testId={`${testIdPrefix}__empty-state-title`}
        className='mt-[20px] text-bw08 font-metricR text-sm'
      />
    </div>
  );
  const renderContent = () => {
    switch (widgetState) {
      case INVOICE_WIDGET_STATES.NOT_EMPTY:
        return renderInvoicesTable();
      case INVOICE_WIDGET_STATES.EMPTY:
        return renderEmptyState();
      case INVOICE_WIDGET_STATES.ZERO:
        return <InvoicesWidgetZeroState handleCreateFirstInvoiceClick={handleCreateFirstInvoiceClick} />;
      default:
        return false;
    }
  };
  const renderFooter = () => (
    <WidgetLink
      linkTitle={t('MANAGE_YOUR_INVOICES')}
      linkUrl={`/${VIEWS_MAP.INVOICES}`}
      testId={`${testIdPrefix}__link`}
      onClick={() => trackMixPanel('Act on Invoices Widget', { Action: 'Manage your Invoices' })}
    />
  );
  if (isLoading) return <InvoicesBillsWidgetsSkeleton isInvoicesWidget />;
  return (
    <div className='w-full h-full bg-bw00 pt-6 px-6 pb-0 rounded-[12px] flex flex-col'>
      <div className='flex justify-between w-full mb-[18px]'>
        <div className='font-metricRSemibold text-bw08 text-sm' data-testid={`${testIdPrefix}__title`}>
          {t('INVOICES_WIDGET_TITLE')}
        </div>
        <div className='flex flex-row gap-[42px]'>
          <TotalPairView
            title={t('TOTAL_OUTSTANDING')}
            value={totalOutstandingInvoiceAmount ? formatValueWithoutCurrency(totalOutstandingInvoiceAmount, 2) : '0'}
            testId={`${testIdPrefix}__totalOutstanding`}
          />
          <TotalPairView
            title={t('TOTAL_INVOICES')}
            value={totalInvoicedAmount ? formatValueWithoutCurrency(totalInvoicedAmount, 2) : '0'}
            testId={`${testIdPrefix}__totalInvoiced`}
          />
        </div>
      </div>
      <div
        className={`h-[193px] border-bw03 ${widgetState === INVOICE_WIDGET_STATES.ZERO ? 'border-t-[1px]' : 'border-y-[1px] '}`}
      >
        {renderContent()}
      </div>
      <div className='h-[64px] w-full justify-end flex self-end'>
        {widgetState !== INVOICE_WIDGET_STATES.ZERO && renderFooter()}
      </div>
    </div>
  );
};
